import { useMemo, useSyncExternalStore } from 'react';
import { CustomizerMenuType } from './constants';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { speakersAnalysisStore } from '@/stores/speakersAnalysis/speakersAnalysis';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { isClipFromUploads, isFullLengthClip } from '@/libs/clipUtils';
import { isSpeakerLayout } from '@/libs/layout';

export enum CLIP_CUSTOMIZER_WARNING_TYPE {
  SPEAKER_FACE_MAPS_INCOMPLETE = 'Some speakers may not have been mapped.'
}

export interface CLIP_CUSTOMIZER_WARNING {
  showWarning: boolean;
  warnings: Partial<Record<CLIP_CUSTOMIZER_WARNING_TYPE, string>>;
}

const defaultWarnings: Record<CustomizerMenuType, CLIP_CUSTOMIZER_WARNING> = {
  [CustomizerMenuType.Layout]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Templates]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Translation]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.AI]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Speakers]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Captions]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Background]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Images]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Videos]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Audio]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.PromptOverride]: { showWarning: false, warnings: {} }
};

function useClipCustomizerWarnings() {
  const { clipId, clipData } = useClipsContext();
  const speakersAnalysis = useSyncExternalStore(speakersAnalysisStore.subscribe, speakersAnalysisStore.getSnapshot);
  const transcriptStore = useTranscriptContext();

  const layoutWarnings = useMemo(() => {
    if (isFullLengthClip(clipData) || !isClipFromUploads(clipData)) {
      return null;
    }

    const speakersAnalysisForClip = speakersAnalysis[clipId];
    if (!speakersAnalysisForClip.accurate) {
      return null;
    }

    if (
      !(clipData.asset_metadata?.scenes && clipData.asset_metadata.scenes.length > 1) &&
      !isSpeakerLayout(clipData.asset_metadata.layout)
    ) {
      return null;
    }

    const speakersWithDetails = transcriptStore.speakersWithDetails;

    // check if there are speakers that don't have face_ids in the speakersAnalysis
    const speakersWithoutFaceIds = Object.values(speakersWithDetails).filter(
      speaker => !speakersAnalysisForClip.speaker_mapping[speaker.id]
    );

    const speakerFaceMapsIncomplete = speakersAnalysisForClip.face_ids.length > 0 && speakersWithoutFaceIds.length > 0;

    return {
      showWarning: speakerFaceMapsIncomplete,
      warnings: {
        ...(speakerFaceMapsIncomplete && {
          [CLIP_CUSTOMIZER_WARNING_TYPE.SPEAKER_FACE_MAPS_INCOMPLETE]:
            CLIP_CUSTOMIZER_WARNING_TYPE.SPEAKER_FACE_MAPS_INCOMPLETE.toString()
        })
      }
    };
  }, [clipId, clipData, speakersAnalysis, transcriptStore.speakersWithDetails]);

  return {
    ...defaultWarnings,
    ...(layoutWarnings && { [CustomizerMenuType.Layout]: layoutWarnings })
  };
}

export default useClipCustomizerWarnings;
